<template>
  <section id="services" class="py-24 bg-gray-900" ref="servicesSection">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header Section -->
      <div class="text-center mb-20" ref="servicesTitle">
        <div class="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {{ $t('services.title') }}
        </div>
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
          {{ $t('services.subtitle') }}
        </h2>
        <p class="text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed text-justify">
          {{ $t('services.description') }}
        </p>
      </div>

      <!-- Services Grid -->
      <div class="flex flex-wrap justify-center gap-4 md:gap-6 mb-16">
        <div 
          v-for="(service, index) in services" 
          :key="index"
          class="group relative bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-400 hover:border-primary/30 cursor-pointer transform hover:-translate-y-1 w-[calc(50%-0.5rem)] md:w-[calc(33.333%-1.33rem)] lg:w-[calc(25%-1.5rem)] max-w-[280px]"
          :ref="el => { if (el) serviceCards[index] = el }"
          @click="selectService(service)"
        >
          <!-- Top Accent -->
          <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-primary-dark to-primary-light"></div>
          
          <!-- Content -->
          <div class="relative p-6 flex flex-col items-center justify-center text-center min-h-[160px]">
            <!-- Icon Section -->
            <div class="mb-4">
              <div class="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
                <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="service.icon"></path>
                </svg>
              </div>
            </div>

            <!-- Title -->
            <h3 class="text-base md:text-lg font-semibold text-gray-100 leading-tight">
              {{ service.title }}
            </h3>
          </div>

          <!-- Hover Effect -->
          <div class="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
        </div>
      </div>

      <!-- Call to Action Section -->
      <div class="text-center">
        <div class="bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-12 text-white relative overflow-hidden">
          <!-- Background Pattern -->
          <div class="absolute inset-0 opacity-10">
            <svg class="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" stroke-width="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)"/>
            </svg>
          </div>
          
          <div class="relative z-10">
            <h3 class="text-xl md:text-2xl font-bold text-white mb-4">
              {{ $t('services.landingCTA.title') }}
            </h3>
            <p class="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {{ $t('services.landingCTA.description') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <button @click="openContactModal" class="bg-white text-primary font-semibold py-4 px-8 rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg">
                {{ $t('services.landingCTA.buttonConsultation') }}
              </button>
              <button @click="openPortfolioModal" class="border-2 border-white text-white font-semibold py-4 px-8 rounded-xl hover:bg-white transform hover:scale-105 transition-all duration-300 text-center">
                {{ $t('services.landingCTA.buttonPortfolio') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Portfolio Coming Soon Modal -->
    <div v-if="showPortfolioModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" @click="closePortfolioModal">
      <div class="bg-gray-800 rounded-3xl max-w-md w-full shadow-2xl" @click.stop>
        <div class="relative">
          <!-- Modal Header -->
          <div class="p-6 lg:p-8 border-b border-gray-700">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-xl lg:text-2xl font-bold text-white mb-2">
                  {{ $t('portfolio.comingSoon.title') }}
                </h3>
              </div>
              <button @click="closePortfolioModal" class="text-gray-400 p-2 hover:bg-gray-700 rounded-full">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="p-6 lg:p-8 text-center">
            <div class="mb-6">
              <svg class="w-20 h-20 mx-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <p class="text-gray-300 text-lg mb-6">
              {{ $t('portfolio.comingSoon.message') }}
            </p>
            <button @click="closePortfolioModal" class="w-full inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-300">
              {{ $t('portfolio.comingSoon.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Form Modal -->
    <div v-if="showContactModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" @click="closeContactModal">
      <div class="bg-gray-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" @click.stop>
        <div class="relative">
          <!-- Modal Header -->
          <div class="p-6 lg:p-8 border-b border-gray-700">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-xl lg:text-2xl font-bold text-white mb-2">
                  {{ $t('contact.form.title') }}
                </h3>
                <p class="text-gray-300">Fill out the form below and we'll get back to you soon.</p>
              </div>
              <button @click="closeContactModal" class="text-gray-400 p-2 hover:bg-gray-700 rounded-full">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Modal Content - Contact Form -->
          <div class="p-6 lg:p-8">
            <!-- Success Message -->
            <div v-if="contactSuccess" class="mb-6 p-4 bg-green-900/50 text-green-300 rounded-lg border border-green-700">
              {{ $t('contact.form.success') }}
            </div>
            
            <!-- Error Message -->
            <div v-if="contactError" class="mb-6 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700">
              {{ contactError }}
            </div>

            <form @submit.prevent="submitContactForm" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="contact-name" class="block text-sm font-medium text-gray-300 mb-2">{{ $t('contact.form.name') }}</label>
                  <input type="text" id="contact-name" v-model="contactForm.name" required
                         class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200">
                  <div v-if="contactFieldErrors.name" class="text-red-400 text-sm mt-1">{{ contactFieldErrors.name }}</div>
                </div>
                <div>
                  <label for="contact-email" class="block text-sm font-medium text-gray-300 mb-2">{{ $t('contact.form.email') }}</label>
                  <input type="email" id="contact-email" v-model="contactForm.email" required
                         class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200">
                  <div v-if="contactFieldErrors.email" class="text-red-400 text-sm mt-1">{{ contactFieldErrors.email }}</div>
                </div>
              </div>
              <div>
                <label for="contact-subject" class="block text-sm font-medium text-gray-300 mb-2">{{ $t('contact.form.subject') }}</label>
                <input type="text" id="contact-subject" v-model="contactForm.subject" required
                       class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200">
                <div v-if="contactFieldErrors.subject" class="text-red-400 text-sm mt-1">{{ contactFieldErrors.subject }}</div>
              </div>
              <div>
                <label for="contact-message" class="block text-sm font-medium text-gray-300 mb-2">{{ $t('contact.form.message') }}</label>
                <textarea id="contact-message" v-model="contactForm.message" rows="5" required
                          class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"></textarea>
                <div v-if="contactFieldErrors.message" class="text-red-400 text-sm mt-1">{{ contactFieldErrors.message }}</div>
              </div>
              <button type="submit" 
                      :disabled="contactIsLoading"
                      class="w-full inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="contactIsLoading">{{ $t('contact.form.submitting') }}</span>
                <span v-else>
                  {{ $t('contact.form.submit') }}
                  <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                  </svg>
                </span>
              </button>
              <!-- reCAPTCHA Protection Badge -->
              <div class="flex items-center justify-center mt-3 text-xs text-gray-500">
                <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
                <span>Protected by reCAPTCHA</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Detail Modal -->
    <div 
      v-if="selectedService" 
      class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
      @click="closeModal"
    >
      <div class="bg-gray-800 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" @click.stop>
        <div class="relative">
          <!-- Modal Header -->
          <div class="p-8 border-b border-gray-700">
            <div class="flex items-start justify-between">
              <div class="flex items-center">
                <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mr-6">
                  <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="selectedService.icon"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-white mb-2">{{ selectedService.title }}</h3>
                  <p class="text-gray-300">{{ selectedService.description }}</p>
                </div>
              </div>
              <button @click="closeModal" class="text-gray-400 p-2 hover:bg-gray-700 rounded-full">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="p-8">
            <!-- Description -->
            <div class="mb-8">
              <p class="text-lg text-gray-700 leading-relaxed">{{ selectedService.description }}</p>
            </div>

            <!-- Learn More Button -->
            <div class="flex justify-center">
              <router-link 
                :to="`/services?service=${selectedService.key}`"
                @click="closeModal"
                class="inline-flex items-center bg-gradient-to-r from-primary to-primary-dark text-white font-semibold py-4 px-8 rounded-xl hover:from-primary-dark hover:to-primary transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {{ $t('services.modal.learnMore') }}
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { textReveal, staggerChildren } from '../utils/animations'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'
import { useRecaptcha } from '../composables/useRecaptcha'

const { t, locale } = useI18n()
const { executeRecaptcha } = useRecaptcha()
const servicesSection = ref(null)
const servicesTitle = ref(null)
const serviceCards = ref([])
const selectedService = ref(null)
const showContactModal = ref(false)
const showPortfolioModal = ref(false)

// Contact form state
const contactForm = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})
const contactIsLoading = ref(false)
const contactError = ref(null)
const contactSuccess = ref(false)
const contactFieldErrors = ref({})

// Rate limiting for contact form
const lastContactSubmissionTime = ref(null)
const contactSubmissionCount = ref(0)
const RATE_LIMIT_WINDOW = 60000 // 1 minute
const MAX_SUBMISSIONS_PER_WINDOW = 3

// Helper function to get array translations
const getArrayTranslation = (key) => {
  const keys = key.split('.')
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  let value = messages
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} at ${k}`)
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

// Service icons mapping (icons are not translatable, so we keep them here)
const serviceIcons = {
  infrastructure: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
  helpdesk: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z',
  project: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  development: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  optimization: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  data: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
  dispatch: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
  itsm: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  itad: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  itam: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
}

// Default icon if service doesn't have one defined
const defaultIcon = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'

// Dynamically generate services from translation files
const services = computed(() => {
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  // Get services.items from translations
  const servicesItems = messages?.services?.items
  
  if (!servicesItems || typeof servicesItems !== 'object') {
    console.warn('No services.items found in translations')
    return []
  }
  
  // Build services array dynamically from translation keys
  return Object.keys(servicesItems)
    .filter(key => {
      // Only include services that have title and description (valid service objects)
      const service = servicesItems[key]
      return service && typeof service === 'object' && service.title && service.description
    })
    .map(key => {
      const service = servicesItems[key]
      return {
        key: key, // Store the key for reference
        title: service.title,
        description: service.description,
        icon: serviceIcons[key] || defaultIcon,
        features: Array.isArray(service.features) ? service.features : [],
        benefits: Array.isArray(service.benefits) ? service.benefits : []
      }
    })
})

const selectService = (service) => {
  selectedService.value = service
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  selectedService.value = null
  document.body.style.overflow = 'auto'
}

const openContactModal = () => {
  showContactModal.value = true
  document.body.style.overflow = 'hidden'
}

const closeContactModal = () => {
  showContactModal.value = false
  document.body.style.overflow = 'auto'
  // Reset form after a delay to allow modal close animation
  setTimeout(() => {
    contactForm.value = { name: '', email: '', subject: '', message: '' }
    contactError.value = null
    contactSuccess.value = false
    contactFieldErrors.value = {}
  }, 300)
}

const openPortfolioModal = () => {
  showPortfolioModal.value = true
  document.body.style.overflow = 'hidden'
}

const closePortfolioModal = () => {
  showPortfolioModal.value = false
  document.body.style.overflow = 'auto'
}

const validateContactForm = () => {
  const errors = {}
  if (!contactForm.value.name || contactForm.value.name.trim().length < 2) {
    errors.name = t('contact.validation.nameRequired')
  }
  if (!contactForm.value.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.value.email)) {
    errors.email = t('contact.validation.emailInvalid')
  }
  if (!contactForm.value.subject || contactForm.value.subject.trim().length < 3) {
    errors.subject = t('contact.validation.subjectRequired')
  }
  if (!contactForm.value.message || contactForm.value.message.trim().length < 10) {
    errors.message = t('contact.validation.messageRequired')
  }
  return errors
}

const checkContactRateLimit = () => {
  const now = Date.now()
  if (lastContactSubmissionTime.value && (now - lastContactSubmissionTime.value) > RATE_LIMIT_WINDOW) {
    contactSubmissionCount.value = 0
  }
  if (contactSubmissionCount.value >= MAX_SUBMISSIONS_PER_WINDOW) {
    const timeRemaining = Math.ceil((RATE_LIMIT_WINDOW - (now - lastContactSubmissionTime.value)) / 1000)
    throw new Error(`Too many submissions. Please wait ${timeRemaining} seconds before trying again.`)
  }
  lastContactSubmissionTime.value = now
  contactSubmissionCount.value++
}

const submitContactForm = async () => {
  contactIsLoading.value = true
  contactError.value = null
  contactSuccess.value = false
  contactFieldErrors.value = {}

  // Validate form
  const errors = validateContactForm()
  if (Object.keys(errors).length > 0) {
    contactFieldErrors.value = errors
    contactIsLoading.value = false
    return
  }

  // Check rate limiting
  try {
    checkContactRateLimit()
  } catch (rateLimitError) {
    contactError.value = rateLimitError.message
    contactIsLoading.value = false
    return
  }

  try {
    // Get reCAPTCHA token
    let recaptchaToken = null
    try {
      recaptchaToken = await executeRecaptcha('contact_form_submit')
    } catch (recaptchaError) {
      console.warn('reCAPTCHA error (continuing anyway):', recaptchaError)
    }

    // Sanitize form data
    const sanitizedForm = {
      name: contactForm.value.name.trim().substring(0, 100),
      email: contactForm.value.email.trim().substring(0, 255),
      subject: contactForm.value.subject.trim().substring(0, 200),
      message: contactForm.value.message.trim().substring(0, 5000),
      recaptchaToken: recaptchaToken,
      timestamp: new Date().toISOString()
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/contact'
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sanitizedForm)
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit form')
    }

    // Show success message
    contactSuccess.value = true
    // Reset form
    contactForm.value = { name: '', email: '', subject: '', message: '' }
    // Auto-close modal after 3 seconds
    setTimeout(() => {
      closeContactModal()
    }, 3000)
  } catch (err) {
    contactError.value = err.message || 'An error occurred. Please try again later.'
    contactSubmissionCount.value = Math.max(0, contactSubmissionCount.value - 1)
  } finally {
    contactIsLoading.value = false
  }
}

onMounted(() => {
  const servicesObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        textReveal(servicesTitle.value, 0)
        staggerChildren(servicesSection.value, '.group', 200)
      }
    })
  }, { threshold: 0.2 })
  
  if (servicesSection.value) {
    servicesObserver.observe(servicesSection.value)
  }
})
</script>

<style scoped>
/* Custom scrollbar for modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #008067;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #0f766e;
}

/* Smooth animations */
.group {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Backdrop blur for modal */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Gradient animations */
@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradient-shift 3s ease infinite;
}

</style> 